import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AddButton, RemoveButton } from '../../reusableItems/buttons';
import { GoEye } from "react-icons/go";
import { RiFileCopyLine } from "react-icons/ri";
import { TailSpin } from 'react-loading-icons';
import { sendRequest, API_ROOT } from '../../../../supplementary_components/requests';
import { addNotification, addPopup } from '../../../../supplementary_components/store';
import { ConfirmationDialogue } from '../../../forms/ConfirmationDialogue';
import { CustomAddForm } from '../../../forms/CustomAddForm';
import { checkForEmptyField } from "../../../forms/Form";
import { getDropBoxFiles } from '../../../forms/input_fields/inputFieldDelegate';
import { can } from '../../../../supplementary_components/userDataDelegate';

const DRIVER_DOCUMENT_TYPE_OPTIONS = [
    { label: "CDL", value: "CDL" },
    { label: "Medical Card", value: "MEDICAL_CARD" },
    { label: "Application", value: "APPLICATION" },
    { label: "TWIC Card", value: "TWIC_CARD" },
    { label: "SSN", value: "SSN" },
    { label: "Passport", value: "PASSPORT" },
    { label: "Green Card", value: "GREEN_CARD" },
    { label: "EAD", value: "EAD" },
    { label: "Clearinghouse", value: "CLEARINGHOUSE" },
    { label: "MVR", value: "MVR" },
    { label: "PSP", value: "PSP" },
];

const DRIVER_DOCUMENT_TYPE_LABELS = DRIVER_DOCUMENT_TYPE_OPTIONS.reduce((acc, opt) => {
    acc[opt.value] = opt.label;
    return acc;
}, {});

const FilesFrame = styled.div`
    min-width: 0;
    overflow: hidden;
    .fileRow{
        margin-bottom:0.6rem;
        overflow:hidden;
        display:flex;
        align-items:center;
        .fileName{
            width: 80%;
            max-width: 80%;
            box-sizing: border-box;
            overflow-wrap: anywhere;
            overflow:hidden;
            cursor:default;
            color:var(--dark-gray);
            font-weight:bold;
        }
        .fileType{
            color:var(--lighter-gray);
            border:solid thin var(--lighter-gray);
            padding:0.1rem 0.3rem;
            border-radius:.3rem;
            margin: 0 0.5rem;
            align-items:flex-end;
            font-size: 0.8rem;
            cursor:default;
            margin-left: auto;
            white-space: nowrap;
        }
        .downloadBtn,
        .copyBtn{
            cursor:pointer;
            display:flex;
            justify-content:center;
            align-items:center;
            color:var(--main-color);
            svg{
                width:1.4rem;
                height:1.4rem;
            }
            padding-right:0.5rem;
        }
    }
    .addButtonRow{
        margin-top:2rem;
    }
`;

const normalizeEntry = (entry) => {
    if (typeof entry === 'string') {
        const trimmed = entry.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
                return JSON.parse(trimmed);
            } catch (e) {
                return { fileName: trimmed, fileType: trimmed, filePath: null };
            }
        }
        return { fileName: trimmed, fileType: trimmed, filePath: null };
    }
    if (entry && typeof entry === 'object') {
        return entry;
    }
    return { fileName: 'Unknown', fileType: 'Unknown', filePath: null };
};

const formatFileTypeLabel = (fileType) => {
    if (!fileType) return 'Unknown';
    return DRIVER_DOCUMENT_TYPE_LABELS[fileType] || fileType;
};

const isImageBlobOrName = (blob, fileName) => {
    const type = (blob?.type || '').toLowerCase();
    const name = (fileName || '').toLowerCase();
    return (
        type.startsWith('image/') ||
        name.endsWith('.jpg') ||
        name.endsWith('.jpeg') ||
        name.endsWith('.png')
    );
};

/** Clipboard write typically needs image/png; convert jpg/etc. first. */
const toPngClipboardBlob = async (blob) => {
    if ((blob.type || '').toLowerCase() === 'image/png') {
        return blob;
    }
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0);
    if (typeof bitmap.close === 'function') {
        bitmap.close();
    }
    return new Promise((resolve, reject) => {
        canvas.toBlob((pngBlob) => {
            if (pngBlob) {
                resolve(pngBlob);
            } else {
                reject(new Error('Failed to convert image'));
            }
        }, 'image/png');
    });
};

const copyImageToClipboard = async (blob) => {
    if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
        throw new Error('Clipboard image copy is not supported in this browser');
    }
    const pngBlob = await toPngClipboardBlob(blob);
    await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': pngBlob }),
    ]);
};

const fetchDriverFileBlob = (driverId, filePath) => new Promise((resolve, reject) => {
    const body = new FormData();
    body.append("filePath", filePath);
    body.append("driverId", driverId);
    const companyHeaders = { companyId: parseInt(localStorage.getItem("currentCompanyId")) };
    sendRequest('POST', API_ROOT + 'drivers/getFile', null, (res) => {
        res.blob().then(resolve).catch(reject);
    }, () => {
        reject(new Error('download failed'));
    }, body, companyHeaders, (b) => b, true);
});

export const DriverFilesListing = ({ files, driverId, setDataMethod }) => {
    const [fileList, setFileList] = useState(files || []);
    const [loadingAction, setLoadingAction] = useState(null); // { index, kind: 'view' | 'copy' }
    const canManageFiles = can('DRIVER_DOCUMENTS', 'MANAGE');

    useEffect(() => {
        setFileList(files || []);
    }, [files]);

    const isLoading = (index, kind) =>
        loadingAction?.index === index && loadingAction?.kind === kind;

    return <FilesFrame>
        {(fileList || []).map((rawEntry, index) => {
            const entry = normalizeEntry(rawEntry);
            const FILE_NAME = entry['fileName'];
            const rowKey = entry.id || `${entry.filePath || FILE_NAME}-${index}`;
            return <div className='fileRow' key={rowKey}>
                <div className='fileName'>{FILE_NAME}</div>
                <div className='fileType'>{formatFileTypeLabel(entry['fileType'])}</div>
                <div className='downloadBtn' title="View" onClick={() => {
                    if (!entry?.filePath) return;
                    setLoadingAction({ index, kind: 'view' });
                    fetchDriverFileBlob(driverId, entry.filePath).then((blob) => {
                        const url = window.URL.createObjectURL(blob);
                        window.open(url);
                        setLoadingAction(null);
                    }).catch(() => {
                        addNotification({ message: `Something went wrong`, type: 'alert' });
                        setLoadingAction(null);
                    });
                }}>
                    {
                        isLoading(index, 'view')
                            ? <TailSpin stroke={'var(--light-gray)'} strokeWidth="0.2rem" width="1rem" height="1rem" speed={1.5} />
                            : <GoEye />
                    }
                </div>
                {canManageFiles ? <RemoveButton action={() => {
                    if (!entry?.filePath) return;
                    addPopup({
                        content: ConfirmationDialogue,
                        popupData: {
                            action: (postResultAction) => {
                                const body = new FormData();
                                body.append("filePath", entry['filePath']);
                                body.append("driverId", driverId);
                                const companyHeaders = { companyId: parseInt(localStorage.getItem("currentCompanyId")) };
                                sendRequest('POST', API_ROOT + 'drivers/removeFile', null, (res) => {
                                    if (setDataMethod && res) {
                                        setDataMethod(res);
                                    } else {
                                        const next = [...fileList];
                                        next.splice(index, 1);
                                        setFileList(next);
                                    }
                                    postResultAction(true);
                                }, () => {
                                    postResultAction(false);
                                    addNotification({ message: `Something went wrong`, type: 'alert' });
                                }, body, companyHeaders, (body) => { return body; }, false);
                            },
                            name: "Confirm",
                            formTitle: "Delete File",
                            submitTitle: "Confirm",
                            method: "POST",
                            message: "Are you sure you want to delete this file?"
                        },
                        postSuccessAction: () => {},
                        EZCloseAllowed: true
                    });
                }} /> : (
                    <div
                        className='copyBtn'
                        title="Copy image to clipboard"
                        onClick={() => {
                            if (!entry?.filePath || isLoading(index, 'copy')) return;
                            setLoadingAction({ index, kind: 'copy' });
                            fetchDriverFileBlob(driverId, entry.filePath).then(async (blob) => {
                                if (!isImageBlobOrName(blob, FILE_NAME)) {
                                    addNotification({
                                        message: `Only images can be copied. Use view to open PDFs.`,
                                        type: 'alert',
                                    });
                                    setLoadingAction(null);
                                    return;
                                }
                                try {
                                    await copyImageToClipboard(blob);
                                    addNotification({
                                        message: `Image copied — paste into your email`,
                                        type: 'notify',
                                        duration: '1500',
                                    });
                                } catch (e) {
                                    addNotification({
                                        message: `Could not copy image to clipboard`,
                                        type: 'alert',
                                    });
                                }
                                setLoadingAction(null);
                            }).catch(() => {
                                addNotification({ message: `Something went wrong`, type: 'alert' });
                                setLoadingAction(null);
                            });
                        }}
                    >
                        {
                            isLoading(index, 'copy')
                                ? <TailSpin stroke={'var(--light-gray)'} strokeWidth="0.2rem" width="1rem" height="1rem" speed={1.5} />
                                : <RiFileCopyLine />
                        }
                    </div>
                )}
            </div>;
        })}
        {canManageFiles ? <div className='addButtonRow'><AddButton customSize={"1.6rem"} action={() => {
            const InputFormData = {
                columns: [
                    {
                        label: "File Type",
                        name: 'fileType',
                        type: 'select',
                        dataset: DRIVER_DOCUMENT_TYPE_OPTIONS.map((o) => o.label),
                        mandatory: true,
                    },
                    {
                        label: "File",
                        name: 'selectedFile',
                        type: 'dropBox',
                        multiple: false,
                        imageEditor: false,
                        fileType: "any",
                        mandatory: true,
                        file: null,
                        files: [],
                    },
                ]
            };
            addPopup({
                content: CustomAddForm,
                popupData: {
                    action: (event, postResultAction) => {
                        event.preventDefault();
                        if (checkForEmptyField(InputFormData, event)) { return; }
                        const dropColumn = InputFormData.columns.find((clmn) => clmn.type === 'dropBox');
                        const selectedFiles = getDropBoxFiles(dropColumn);
                        if (!selectedFiles.length) {
                            return;
                        }
                        if (selectedFiles.length > 1) {
                            addNotification({ message: `Upload one file at a time`, type: 'alert' });
                            return;
                        }
                        const selectedLabel = event.target['fileType'].value;
                        const mapped = DRIVER_DOCUMENT_TYPE_OPTIONS.find((o) => o.label === selectedLabel);
                        const mappedFileType = mapped ? mapped.value : null;
                        if (!mappedFileType) {
                            addNotification({ message: `Unsupported file type`, type: 'alert' });
                            return;
                        }
                        const companyHeaders = { companyId: parseInt(localStorage.getItem("currentCompanyId")) };
                        const body = new FormData();
                        body.append("file", selectedFiles[0]);
                        body.append("fileName", selectedFiles[0].name);
                        body.append("driverId", driverId);
                        body.append("fileType", mappedFileType);
                        sendRequest('POST', API_ROOT + 'drivers/addFile', null, (res) => {
                            if (setDataMethod && res) {
                                setDataMethod(res);
                            }
                            postResultAction(true);
                        }, () => {
                            postResultAction(false);
                            addNotification({ message: `Something went wrong`, type: 'alert' });
                        }, body, companyHeaders, (body) => { return body; }, false);
                    },
                    name: "Confirm",
                    formTitle: "Add File",
                    submitTitle: "Add",
                    method: "POST",
                    columns: InputFormData['columns']
                },
                EZCloseAllowed: true
            });
        }} /></div> : null}
    </FilesFrame>;
};
